import {
  updateCustomerList as updateCustomerListPages,
  updateIsError as updateCustomerListPagesError,
  updateIsFetching as updateCustomerListPageFetching,
} from "../../../reducers/customer/customers";
import {
  updateCustomerList as updateNewCustomer,
  updateIsError as updateNewCustomerError,
  updateIsFetching as updateNewCustomerFetching,
} from "../../../reducers/customer/newCustomers";
import { accAPI } from "../..";
import {
  CustomerListQueryParams,
  CustomerListRTKQueryParams,
  CustomerListPostParams,
  CustomerListPostBody,
  CustomersResponse,
} from "./customerListAPI.types";

const getQueryParamsString = ({
  pageNum,
  pageSize,
  period,
  sortValue,
  sortOrder,
  searchBy,
  locale,
  isNew,
  isVerified,
  isCurrentMonthRenewalDate,
  customerIds,
  confidentialUserText,
  businessNature,
  requestWithSponsor,
}: CustomerListQueryParams) => {
  const parsedParams = new URLSearchParams({
    PageNum: `${pageNum}`,
    PageSize: `${pageSize}`,
    Period: `${period}`,
    SortValue: `${sortValue}`,
    SortOrder: `${sortOrder}`,
  });

  // optional fields
  if (searchBy !== void 0 && searchBy.length > 0) {
    parsedParams.append("SearchBy", searchBy);
  }

  if (locale !== void 0) {
    parsedParams.append("Locale", `${locale}`);
  }

  if (isNew !== void 0) {
    parsedParams.append("IsNew", `${isNew}`);
  }

  if (isVerified !== void 0) {
    parsedParams.append("IsVerified", `${isVerified}`);
  }

  if (customerIds !== void 0) {
    parsedParams.append("CustomerIDs", `${customerIds}`);
  }

  if (confidentialUserText !== void 0 && confidentialUserText.length > 0) {
    parsedParams.append("ConfidentialUserText", confidentialUserText);
  }

  if (businessNature !== void 0) {
    parsedParams.append("BusinessNature", `${businessNature}`);
  }

  if (isCurrentMonthRenewalDate !== void 0) {
    parsedParams.append("IsCurrentMonthRenewalDate", `${isCurrentMonthRenewalDate}`);
  }

  if (requestWithSponsor === true) {
    parsedParams.append("RequestWithSponsor", "true");
  }

  return parsedParams.toString();
};

const getURL = ({ affAbo, ...queryParams }: CustomerListRTKQueryParams) =>
  `/customer-v3/api/${affAbo}?${getQueryParamsString(queryParams)}`;

const buildPostBody = (params: CustomerListPostParams): { url: string; body: CustomerListPostBody } => {
  const body: CustomerListPostBody = {
    PageNum: params.pageNum ?? 1,
    PageSize: params.pageSize,
    Period: Number(params.period),
    SortValue: params.sortValue,
    SortOrder: params.sortOrder,
    EnabledFilters: params.enabledFilters,
    EnableGuestCustomer: params.enableGuestCustomer,
  };

  if (params.searchBy !== void 0 && params.searchBy.length > 0) {
    body.SearchBy = params.searchBy;
  }

  if (params.locale !== void 0) {
    body.Locale = params.locale;
  }

  if (params.confidentialUserText !== void 0 && params.confidentialUserText.length > 0) {
    body.ConfidentialUserText = params.confidentialUserText;
  }

  return {
    url: `/customer-v3/api/${params.affAbo}`,
    body,
  };
};

export const customerListAPI = accAPI.injectEndpoints({
  endpoints: (build) => ({
    getCustomerList: build.query<CustomersResponse, CustomerListRTKQueryParams>({
      query: (params) => getURL(params),
    }),
    // POST mutation with customers reducer side-effect — mutations always execute (no caching issues)
    postCustomerListPages: build.mutation<CustomersResponse, CustomerListPostParams>({
      query: (params) => {
        const { url, body } = buildPostBody(params);
        return {
          url,
          method: "POST",
          body,
        };
      },
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        const requestId = `post-${Date.now()}`;
        try {
          dispatch(updateCustomerListPageFetching({ requestId, isFetching: true }));
          const { data } = await queryFulfilled;
          dispatch(updateCustomerListPages({ requestId, data }));
        } catch (error) {
          dispatch(updateCustomerListPagesError({ requestId, isError: true }));
        }
      },
    }),
    // GET query with customers reducer side-effect (kept for backward compatibility)
    getCustomerListPages: build.query<CustomersResponse, CustomerListRTKQueryParams>({
      query: (params) => getURL(params),
      extraOptions: {
        maxRetries: 3,
        backoff: (attempt: number) => new Promise((resolve) => setTimeout(resolve, attempt * 1000)),
      },
      async onQueryStarted(_args, { dispatch, queryFulfilled, requestId }) {
        try {
          dispatch(updateCustomerListPageFetching({ requestId, isFetching: true }));
          const { data } = await queryFulfilled;
          dispatch(updateCustomerListPages({ requestId, data }));
        } catch (error) {
          dispatch(updateCustomerListPagesError({ requestId, isError: true }));
        }
      },
    }),
    // query with newCustomers reducer side-effect (stays on GET - overview page)
    getNewCustomerList: build.query<CustomersResponse, CustomerListRTKQueryParams>({
      query: (params) => getURL(params),
      async onQueryStarted(_args, { dispatch, queryFulfilled, requestId }) {
        try {
          dispatch(updateNewCustomerFetching({ requestId, isFetching: true }));
          const { data } = await queryFulfilled;
          dispatch(updateNewCustomer({ requestId, data }));
        } catch (error) {
          dispatch(updateNewCustomerError({ requestId, isError: true }));
        }
      },
    }),
  }),
});

export const {
  useGetCustomerListQuery,
  usePostCustomerListPagesMutation,
  useLazyGetCustomerListPagesQuery,
  useLazyGetNewCustomerListQuery,
} = customerListAPI;
